import { API_BASE_URL } from './config';
import { authStore } from './authStore';
import { onlineStore } from './onlineStore';

// Nutzeranfrage: bei schlechter (statt fehlender) Verbindung liefert der
// Browser kein 'offline'-Event - ohne Timeout blieb ein Request (z. B.
// "Kaufen" im Einkaufsmodus) dadurch unbegrenzt hängen, statt zuverlässig in
// die Offline-Warteschlange zu fallen.
const REQUEST_TIMEOUT_MS = 10000;

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export class OfflineError extends Error {
  constructor() {
    super('Keine Verbindung - Anfrage konnte nicht gesendet werden.');
  }
}

interface RequestOptions extends RequestInit {
  idempotencyKey?: string;
  clientTimestamp?: string;
  skipAuthRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const { refreshToken } = authStore.getState();
  if (!refreshToken) return false;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
        if (!res.ok) {
          authStore.clear();
          return false;
        }
        const body = await res.json();
        authStore.setAccessToken(body.tokens.accessToken, body.tokens.refreshToken);
        authStore.setUser(body.user);
        return true;
      } catch {
        return false; // offline - Sitzung bleibt lokal gültig (AUTH-004), kein Logout erzwingen.
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { idempotencyKey, clientTimestamp, skipAuthRefresh, ...init } = options;
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  const { accessToken } = authStore.getState();
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);
  if (clientTimestamp) headers.set('X-Client-Timestamp', clientTimestamp);
  headers.set('X-Client-Origin', 'web-pwa');

  // Datei-Uploads (FormData) dürfen auf einer schwachen Verbindung länger
  // dauern als normale JSON-Requests, bevor sie als fehlgeschlagen gelten.
  const timeoutMs = init.body instanceof FormData ? REQUEST_TIMEOUT_MS * 3 : REQUEST_TIMEOUT_MS;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers, signal: init.signal ?? AbortSignal.timeout(timeoutMs) });
  } catch {
    // Sowohl ein echter Verbindungsabbruch als auch ein Timeout auf einer
    // langsamen/schlechten Verbindung landen hier - beides wird wie
    // "offline" behandelt, damit z. B. queuedMutation() zuverlässig in die
    // Warteschlange fällt statt unbegrenzt zu hängen.
    onlineStore.reportConnectivityFailure();
    throw new OfflineError();
  }
  onlineStore.reportSuccess();

  if (res.status === 401 && !skipAuthRefresh && authStore.getState().refreshToken) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return apiFetch<T>(path, { ...options, skipAuthRefresh: true });
    }
  }

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => undefined) : undefined;

  if (!res.ok) {
    const err = body?.error ?? { code: 'UNKNOWN', message: `Fehler ${res.status}` };
    throw new ApiError(res.status, err.code, err.message, err.details);
  }
  return body as T;
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
